import { useCallback, useState } from "react";

export type SuccessSecondaryAction = {
  label: string;
  onClick: () => void;
};

type SuccessState = {
  open: boolean;
  title: string;
  onClose: () => void;
  secondaryAction?: SuccessSecondaryAction;
};

type ShowSuccessOptions = {
  onClose?: () => void;
  secondaryAction?: SuccessSecondaryAction;
};

export const useSuccessDialog = () => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [afterClose, setAfterClose] = useState<null | (() => void)>(null);
  const [secondaryAction, setSecondaryAction] = useState<SuccessSecondaryAction | undefined>(undefined);

  /**
   * `cbOrOpts` accepts either the original bare callback (every existing call site) or an options
   * object carrying `secondaryAction` — a second button that hands off to another flow instead of
   * finishing this one (e.g. "Folgetermin planen" opening the plan overlay).
   */
  const showSuccess = useCallback((message: string, cbOrOpts?: (() => void) | ShowSuccessOptions) => {
    const opts: ShowSuccessOptions = typeof cbOrOpts === "function" ? { onClose: cbOrOpts } : (cbOrOpts ?? {});
    setTitle(message);
    setAfterClose(() => opts.onClose ?? null);
    setSecondaryAction(opts.secondaryAction);
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    afterClose?.();
    setAfterClose(null);
    setSecondaryAction(undefined);
  }, [afterClose]);

  /**
   * Closes without running `onClose`'s callback — for a secondary action that hands off to another
   * flow (opening a plan overlay) rather than finishing this one. Using `onClose` there would also
   * fire whatever the primary flow wired up to run on close.
   */
  const dismiss = useCallback(() => {
    setOpen(false);
    setAfterClose(null);
    setSecondaryAction(undefined);
  }, []);

  const dialogProps: SuccessState = { open, title, onClose, secondaryAction };

  return { showSuccess, dialogProps, dismiss };
};
